<script lang="ts" setup>
import type { UICodeProps } from "~/components/ui/UICode.vue";
import { faListTree, faCode, faTable } from "@fortawesome/pro-light-svg-icons";
import type { UiDataTableProps } from "~/components/ui/DataTable.vue";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

interface UiDataDisplayProps {
    data: any;
    defaultTab?: "table" | "code";
    lang?: UICodeProps["lang"];
    codeConfig?: Partial<
        UICodeProps & { class?: string | (string | undefined)[] | undefined }
    >;
    tableConfig?: Partial<
        UiDataTableProps & {
            class?: string | (string | undefined)[] | undefined;
        }
    >;
    extra?: { label: string; tabIcon: IconDefinition; slot: string }[];
}

const props = withDefaults(defineProps<UiDataDisplayProps>(), {
    defaultTab: "table",
    lang: "json",
    codeConfig: () => ({}),
    tableConfig: () => ({}),
    extra: () => [],
});

const tabs = computed(() => [
    {
        label: "Data Table",
        tabIcon: faTable,
        slot: "table",
    },
    {
        label: "Tree",
        tabIcon: faListTree,
        slot: "tree",
    },
    {
        label: "Raw Data",
        tabIcon: faCode,
        slot: "code",
    },
    ...props.extra.map((item) => ({
        ...item,
        slot: "extra",
        actualSlot: item.slot,
    })),
]);

const tab = ref(tabs.value.findIndex((t) => t.slot === props.defaultTab));

function transformLabel(label: string) {
    label = label.toString();

    const match = label.match(/^\[\d+\]_/);

    if (match) {
        label = label.replace(match[0], "");
    }

    return label.replaceAll("_", " ");
}
</script>

<template>
    <UTabs class="flex" :items="tabs" v-model="tab" orientation="vertical" :ui="{
        wrapper: 'flex items-start space-y-0 pt-2 gap-4',
        list: {
            width: 'w-10',
            background: 'bg-hd-neutral-50',
        },
    }">
        <template #default="{ item, index, selected }">
            <FontAwesomeIcon :icon="item.tabIcon" class="w-4 h-4 flex-shrink-0" />
        </template>
        <template #table>
            <UiDataTable :items="data" v-bind="tableConfig" hide-empty placement="top"
                class="bg-hd-neutral-50 rounded-xl" label-class="whitespace-nowrap capitalize"
                :transform-label="transformLabel" :ui="{
                    tbody: 'divide-y divide-hd-neutral-300',
                }" :sub="{
                    ui: {
                        wrapper: '-my-4 -mx-4 relative overflow-x-auto',
                        tbody: 'border-l border-hd-neutral-300 divide-y divide-hd-neutral-300',
                    },
                }" />
        </template>
        <template #tree>
            <div class="w-full bg-hd-neutral-50 px-4 py-3 rounded-xl">
                <UiDataTree :items="data" v-bind="tableConfig" hide-empty placement="top"
                    class="bg-hd-neutral-50 rounded-xl" label-class="whitespace-nowrap capitalize" :transform-label="(label) => label.toString().replaceAll('_', ' ')
                        " :ui="{
                        tbody: 'divide-y divide-hd-neutral-300',
                    }" :sub="{
                        ui: {
                            wrapper: '-my-4 -mx-4 relative overflow-x-auto',
                            tbody: 'border-l border-hd-neutral-300 divide-y divide-hd-neutral-300',
                        },
                    }" />
            </div>
        </template>
        <template #code>
            <UICode v-bind="codeConfig" :lang="lang" :code="data" class="rounded-xl" width="auto" />
        </template>
        <template #extra>
            <slot :name="tabs[tab].actualSlot" :data="data" />
        </template>
    </UTabs>
</template>
